import { useState } from "react";
import PropTypes from "prop-types";
import { Button, Input, Radio, SettingsCard, Space } from "top-component-library";
import DeleteStationDialog from "./DeleteStationDialog";
import { FormRow, SettingHint } from "../styles";

const ITEM_CLASS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "food", label: "Food only" },
  { value: "drinks", label: "Drinks only" },
];

// Mirrors editCustomerProperties.html's "Stations" card. A station with received orders
// requires typing its name to confirm deletion (DeleteStationDialog); one with none deletes
// immediately, matching confirmDeleteStation() exactly.
const StationsCard = ({ stations, saving, onAddStation, onDeleteStation }) => {
  const [addingStation, setAddingStation] = useState(false);
  const [stationName, setStationName] = useState("");
  const [itemClass, setItemClass] = useState(null);
  const [stationPendingDelete, setStationPendingDelete] = useState(null);

  const handleAdd = async () => {
    const succeeded = await onAddStation(stationName, itemClass);
    if (succeeded) {
      setStationName("");
      setItemClass(null);
      setAddingStation(false);
    }
  };

  const handleDeleteClick = (station) => {
    if (station.has_orders) {
      setStationPendingDelete(station);
    } else {
      onDeleteStation(station);
    }
  };

  return (
    <SettingsCard title="Stations">
      <SettingHint>
        List of stations for this customer. Once a new station has been created here you should change its properties on
        the &apos;Check Status of All Tablets and Printers&apos; page. You cannot delete the last station at a customer.
      </SettingHint>
      {stations.map((station) => (
        <FormRow key={station.id}>
          <label>{station.station_name}</label>
          <SettingHint>{station.has_orders ? "Has received orders" : "Has never received orders"}</SettingHint>
          <Button danger onClick={() => handleDeleteClick(station)}>
            Delete
          </Button>
        </FormRow>
      ))}
      {!addingStation && <Button onClick={() => setAddingStation(true)}>Add New Station</Button>}
      {addingStation && (
        <FormRow>
          <label htmlFor="customer-properties-new-station-name">Station Name</label>
          <Input
            id="customer-properties-new-station-name"
            value={stationName}
            onChange={(event) => setStationName(event.target.value)}
          />
          <SettingHint>
            When uploading a menu CSV, which items should be attached to this station by default?
          </SettingHint>
          <Radio.Group value={itemClass} onChange={(event) => setItemClass(event.target.value)}>
            <Space direction="vertical">
              {ITEM_CLASS_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          <Space>
            <Button type="primary" loading={saving} disabled={!stationName || !itemClass} onClick={handleAdd}>
              Add Station
            </Button>
            <Button onClick={() => setAddingStation(false)}>Cancel</Button>
          </Space>
        </FormRow>
      )}
      {stationPendingDelete && (
        <DeleteStationDialog
          station={stationPendingDelete}
          onClose={() => setStationPendingDelete(null)}
          onConfirm={onDeleteStation}
        />
      )}
    </SettingsCard>
  );
};

StationsCard.propTypes = {
  stations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      station_name: PropTypes.string.isRequired,
      has_orders: PropTypes.bool.isRequired,
    })
  ).isRequired,
  saving: PropTypes.bool.isRequired,
  onAddStation: PropTypes.func.isRequired,
  onDeleteStation: PropTypes.func.isRequired,
};

export default StationsCard;
